import { supabase } from '@/integrations/supabase/client';

const SESSION_KEY = 'sumsae_analytics_session_id';
const LAST_EVENT_KEY = 'sumsae_analytics_last_path';

function generateSessionId(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const newId = generateSessionId();
    sessionStorage.setItem(SESSION_KEY, newId);
    return newId;
  } catch {
    return generateSessionId();
  }
}

function getLastPath(): string | null {
  try {
    return sessionStorage.getItem(LAST_EVENT_KEY);
  } catch {
    return null;
  }
}

function setLastPath(path: string): void {
  try {
    sessionStorage.setItem(LAST_EVENT_KEY, path);
  } catch {
    // ignore
  }
}

function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

let flushTimeout: ReturnType<typeof setTimeout> | null = null;
const pendingEvents: Array<{
  session_id: string;
  path: string;
  referrer: string | null;
  user_agent: string;
  screen_width: number;
  screen_height: number;
  is_mobile: boolean;
  user_id: string | null;
}> = [];

async function flushEvents() {
  if (pendingEvents.length === 0) return;
  const batch = pendingEvents.splice(0, pendingEvents.length);

  try {
    await supabase.from('page_events').insert(
      batch.map((event) => ({
        session_id: event.session_id,
        path: event.path,
        referrer: event.referrer,
        user_agent: event.user_agent,
        screen_width: event.screen_width,
        screen_height: event.screen_height,
        is_mobile: event.is_mobile,
        user_id: event.user_id,
      }))
    );
  } catch (error) {
    console.error('Failed to record page events:', error);
  }
}

function scheduleFlush() {
  if (flushTimeout) clearTimeout(flushTimeout);
  flushTimeout = setTimeout(() => {
    flushEvents();
  }, 500);
}

function normalizePath(rawPath: string): string {
  const withoutQuery = rawPath.split('?')[0].split('#')[0];
  if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery || '/';
}

export async function recordPageView(
  rawPath: string,
  userId: string | null = null
): Promise<void> {
  if (typeof window === 'undefined') return;

  const path = normalizePath(rawPath);
  const lastPath = getLastPath();
  if (lastPath === path) return;
  setLastPath(path);

  const event = {
    session_id: getSessionId(),
    path,
    referrer: document.referrer || null,
    user_agent: navigator.userAgent,
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
    is_mobile: isMobileDevice(),
    user_id: userId,
  };

  pendingEvents.push(event);
  scheduleFlush();
}

export function flushAnalytics(): Promise<void> {
  return flushEvents();
}
